<main class="invoice-detail" aria-labelledby="invoice-title">
  <header class="invoice-detail__heading">
    <button mat-stroked-button type="button" (click)="goBack()"><mat-icon>arrow_back</mat-icon>{{ "invoice_back" | translate }}</button>
    <div>
      <h1 id="invoice-title">{{ "invoice_title" | translate }}</h1>
      <p>{{ "invoice_intro" | translate }}</p>
    </div>
  </header>

  @if (hasLoadError) {
    <div class="invoice-state invoice-state--error" role="alert">{{ "invoice_load_error" | translate }}</div>
  } @else if (!hasLoaded) {
    <div class="invoice-state"><mat-spinner diameter="40"></mat-spinner></div>
  } @else if (job) {
    <section class="invoice-section job-summary" aria-labelledby="job-details-title">
      <h2 id="job-details-title">{{ "invoice_job_details" | translate }}</h2>
      <dl>
        <div><dt>{{ "invoice_po_numbers" | translate }}</dt><dd>{{ purchaseOrderNumbers }}</dd></div>
        <div><dt>{{ "invoice_job_number" | translate: { type: (job.jobType || '') } }}</dt><dd>{{ jobNumber }}</dd></div>
        <div><dt>{{ "invoice_site_name" | translate }}</dt><dd>{{ job.site?.name || '-' }}</dd></div>
        <div><dt>{{ "invoice_site_code" | translate }}</dt><dd>{{ job.site?.code || '-' }}</dd></div>
        <div><dt>{{ "invoice_source" | translate }}</dt><dd>{{ job.source || '-' }}</dd></div>
        <div><dt>{{ "invoice_business_unit" | translate }}</dt><dd>{{ job.purchaseOrders[0]?.businessUnit || '-' }}</dd></div>
      </dl>
    </section>

    <section class="invoice-section" aria-labelledby="invoice-files-title">
      <div class="section-heading"><div><h2 id="invoice-files-title">{{ "invoice_files" | translate }}</h2><p>{{ "invoice_files_help" | translate }}</p></div><mat-icon>receipt_long</mat-icon></div>
      <ng-container *ngTemplateOutlet="filePicker; context: { category: 'invoice', required: true, accept: attachmentRules.invoice.accept }"></ng-container>
    </section>

    <section class="invoice-section" aria-labelledby="supporting-files-title">
      <div class="section-heading"><div><h2 id="supporting-files-title">{{ "invoice_supporting_documents" | translate }}</h2><p>{{ "invoice_supporting_help" | translate }}</p></div><mat-icon>attach_file</mat-icon></div>
      <ng-container *ngTemplateOutlet="filePicker; context: { category: 'supporting', required: false, accept: attachmentRules.supporting.accept }"></ng-container>
    </section>

    <section class="invoice-section" aria-labelledby="service-sheets-title">
      <div class="section-heading"><div><h2 id="service-sheets-title">{{ "invoice_service_sheets" | translate }}</h2><p>{{ "invoice_service_sheets_help" | translate }}</p></div><mat-icon>description</mat-icon></div>
      <ng-container *ngTemplateOutlet="filePicker; context: { category: 'serviceSheet', required: false, accept: attachmentRules.serviceSheet.accept }"></ng-container>
    </section>

    @if (attachmentError) { <p class="form-error" role="alert">{{ attachmentError | translate }}</p> }

    <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
      <section class="invoice-section" aria-labelledby="invoice-details-title">
        <h2 id="invoice-details-title">{{ "invoice_details" | translate }}</h2>
        <div class="invoice-fields">
          <div class="billing-summary">
            <h3>{{ "invoice_bill_to" | translate }}</h3>
            <p>{{ job.purchaseOrders[0]?.legalEntity || ("invoice_not_available" | translate) }}</p>
            @if (job.purchaseOrders[0]?.businessUnit) { <p>{{ job.purchaseOrders[0]?.businessUnit }}</p> }
          </div>
          <div class="invoice-inputs">
            <mat-form-field appearance="outline">
              <mat-label>{{ "invoice_number" | translate }}</mat-label>
              <input matInput formControlName="invoiceNumber" maxlength="100">
              @if (form.controls.invoiceNumber.touched && form.controls.invoiceNumber.invalid) { <mat-error>{{ "invoice_number_required" | translate }}</mat-error> }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>{{ "invoice_date" | translate }}</mat-label>
              <input matInput [matDatepicker]="invoiceDatePicker" formControlName="invoiceDate" [max]="maxInvoiceDate">
              <mat-datepicker-toggle matIconSuffix [for]="invoiceDatePicker"></mat-datepicker-toggle>
              <mat-datepicker #invoiceDatePicker></mat-datepicker>
              @if (form.controls.invoiceDate.hasError('matDatepickerMax')) {
                <mat-error>{{ "invoice_date_future_error" | translate }}</mat-error>
              } @else if (form.controls.invoiceDate.touched && form.controls.invoiceDate.invalid) {
                <mat-error>{{ "invoice_date_required" | translate }}</mat-error>
              }
            </mat-form-field>
          </div>
        </div>

        @for (purchaseOrder of job.purchaseOrders; track purchaseOrder.id) {
          <section class="po-lines">
            <h3>{{ "invoice_line_items_for_po" | translate: { number: (purchaseOrder.purchaseOrderNumber || purchaseOrder.purchaseOrderReferenceId || '-') } }}</h3>
            <div class="line-table-wrap">
              <table class="line-table">
                <thead><tr><th>{{ "invoice_type" | translate }}</th><th>{{ "invoice_description" | translate }}</th><th>{{ "invoice_unit_price" | translate }}</th><th>{{ "invoice_unit_tax" | translate }}</th><th>{{ "invoice_quantity" | translate }}</th><th>{{ "invoice_gross" | translate }}</th><th><span class="visually-hidden">{{ "invoice_line_actions" | translate }}</span></th></tr></thead>
                <tbody>
                  @for (line of lineItems(purchaseOrder); track line.id) {
                    <tr>
                      <td>{{ line.itemCode || '-' }}</td><td>{{ line.description || '-' }}</td>
                      <td>{{ line.unitPriceExcludingTax | currency: currencyCode(purchaseOrder.currencyCode) }}</td>
                      <td>{{ line.unitTax | currency: currencyCode(purchaseOrder.currencyCode) }}</td>
                      <td>{{ line.quantity }}</td>
                      <td>{{ lineGross(line) | currency: currencyCode(purchaseOrder.currencyCode) }}</td>
                      <td class="line-actions">
                        <button mat-icon-button type="button" (click)="openEditLineItem(purchaseOrder, line)" [attr.aria-label]="'invoice_edit_line' | translate"><mat-icon>edit</mat-icon></button>
                        @if (line.isAdded) { <button mat-icon-button type="button" (click)="removeLineItem(purchaseOrder, line)" [attr.aria-label]="'invoice_remove_line' | translate"><mat-icon>delete</mat-icon></button> }
                      </td>
                    </tr>
                  } @empty {
                    <tr><td colspan="7">{{ "invoice_no_line_items" | translate }}</td></tr>
                  }
                </tbody>
                <tfoot><tr><th colspan="5">{{ "invoice_po_total" | translate }}: {{ purchaseOrderNet(purchaseOrder) | currency: currencyCode(purchaseOrder.currencyCode) }} + {{ purchaseOrderTax(purchaseOrder) | currency: currencyCode(purchaseOrder.currencyCode) }} {{ "invoice_total_tax" | translate }}</th><td>{{ purchaseOrderGross(purchaseOrder) | currency: currencyCode(purchaseOrder.currencyCode) }}</td><td></td></tr></tfoot>
              </table>
            </div>
            <button mat-stroked-button type="button" class="add-line-button" (click)="openAddLineItem(purchaseOrder)"><mat-icon>add</mat-icon>{{ "invoice_add_line_item" | translate }}</button>
          </section>
        }

        <section class="invoice-totals" aria-labelledby="invoice-totals-title">
          <h3 id="invoice-totals-title">{{ "invoice_totals" | translate }}</h3>
          @for (currency of currencies; track currency) {
            <div class="invoice-totals__row">
              <div><span>{{ "invoice_total_net" | translate }} ({{ currency }})</span><strong>{{ totalNet(currency) | currency: currency }}</strong></div>
              <div><span>{{ "invoice_total_tax" | translate }} ({{ currency }})</span><strong>{{ totalTax(currency) | currency: currency }}</strong></div>
              <div><span>{{ "invoice_total_gross" | translate }} ({{ currency }})</span><strong>{{ totalGross(currency) | currency: currency }}</strong></div>
              <div><span>{{ "invoice_average_po_cost" | translate }} ({{ currency }})</span><strong>{{ averagePurchaseOrderCost(currency) | currency: currency }}</strong></div>
            </div>
          }
        </section>
      </section>

      @if (form.touched && attachments.invoice.length === 0) { <p class="form-error" role="alert">{{ "invoice_file_required" | translate }}</p> }
      @if (submissionUnavailable) { <p class="submission-notice" role="alert">{{ "invoice_submission_unavailable" | translate }}</p> }

      <footer class="invoice-submit">
        <p><mat-icon>info</mat-icon>{{ "invoice_check_information" | translate }}</p>
        <button mat-raised-button type="submit"><mat-icon>cloud_upload</mat-icon>{{ "invoice_submit" | translate }}</button>
      </footer>
    </form>
  }
</main>

<ng-template #filePicker let-category="category" let-required="required" let-accept="accept">
  <p class="file-rule"><mat-icon>info</mat-icon>{{ (category === 'invoice' ? "invoice_pdf_rule" : "invoice_document_rule") | translate }}</p>
  <div class="file-list">
    @for (file of attachmentFiles(category); track file.name; let index = $index) {
      <div class="file-chip"><mat-icon>insert_drive_file</mat-icon><span>{{ file.name }}</span><button mat-icon-button type="button" (click)="removeFile(category, index)" [attr.aria-label]="'invoice_remove_file' | translate"><mat-icon>delete</mat-icon></button></div>
    }
  </div>
  <label class="file-button" mat-stroked-button><mat-icon>add</mat-icon>{{ "invoice_add_file" | translate }}<input type="file" hidden multiple [accept]="accept" (change)="selectFiles($event, category)"></label>
  @if (required && attachmentFiles(category).length === 0) { <span class="required-note">{{ "invoice_required" | translate }}</span> }
</ng-template>

